import React, { useMemo, useState, useEffect } from "react";
import { SLAandAlerts } from "../../Layout/Publish/SLAandAlerts/SLAandAlerts";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Button,
  Tooltip,
} from "@mui/material";
import PermissionTooltip from "../../toolTipComponenet/TooltipComponent.jsx";
import { SquarePen, Eye, EyeClosed , PenBox} from 'lucide-react';
import { MoreVertical, Bell, GitBranch, Trash2, Upload, CirclePlay, Download } from 'lucide-react';
import { Link, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import axios from "axios";
import heartLoading from "../../../assests/heartLoading.gif";
import { isProdEnv, isEngEnv } from "../../../env-utils.js";
import EditProductModal from "./ProductViewComponents/EditProductModal.jsx";
import DQModal from "./DQmodal.jsx";
import exportService from "../../../services/exportService.js";
import { useExport } from "../../../context/ExportContext.jsx";

const MuiDataTable = ({
  data,
  columns,
  onRefresh,
  onEdit,
  onView,
  onDelete,
  selectedView,
  searchQuery,
  userRole,
}) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [versioningOpen, setVersioningOpen] = useState(false);
  const [versioningProduct, setVersioningProduct] = useState(null);
  const [versioningId, setVersioningId] = useState(null);
  const [toast, setToast] = React.useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();
  const [openAlertsModal, setOpenAlertsModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const isProd = useMemo(() => isProdEnv(), []);
  const isEng = useMemo(() => isEngEnv(), []);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [dqModal, setDqModal] = useState(false);
  const [dqProduct, setDqProduct] = useState({ productId: null, productName: "" });
  const [exportingProducts, setExportingProducts] = useState(new Set());
  const { startExportPolling } = useExport();

  const userEmail = localStorage.getItem("userId");
  const promoteUserRole = userRole === "Data Steward" || userRole === "Product Owner";
  const [userProductNames, setUserProductNames] = useState([]);

  useEffect(() => {
    const fetchUserProducts = async () => {
      if (!userEmail) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/id/${userEmail}`);
        setUserProductNames(response.data?.product_names || []);
      } catch (error) {
        console.error("Error fetching user product names:", error);
        setUserProductNames([]);
      }
    };
    fetchUserProducts();
  }, [userEmail]);

  const handleMenuOpen = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const showToast = (msg) => {
    setToast((prev) => ({ ...prev, open: true, message: msg, severity: "success" }));
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
  };

  const showFailedToast = (msg) => {
    setToast((prev) => ({ ...prev, open: true, message: msg, severity: "error" }));
    setTimeout(() => setToast((prev) => ({ ...prev, open: false })), 3000);
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  const handleOpenDQModal = (row) => {
    setDqProduct({ productId: row.productId, productName: row.productName });
    setDqModal(true);
    handleMenuClose();
  };

  const handleExportClick = async (row) => {
    const { productId, productName } = row;
    handleMenuClose();
    setExportingProducts((prev) => new Set(prev).add(productId));

    const stopExporting = () =>
      setExportingProducts((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });

    try {
      const { job_id } = await exportService.triggerExport(productName, userEmail);
      startExportPolling(job_id, `"${productName}"`, {
        onComplete: stopExporting,
        onError: stopExporting,
      });
    } catch {
      stopExporting();
      showFailedToast(`Failed to trigger export for "${productName}"`);
    }
  };

  const handleAlertsClick = async (row) => {
    setSelectedRowData(row);
    setAlertsData(null);
    setAlertsLoading(true);
    setOpenAlertsModal(true);

    try {
      const alertsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/alerts/get/${row.productId}`
      );

      const groupedAlerts = {};
      alertsResponse.data.forEach((alert) => {
        if (!groupedAlerts[alert.alert_id]) {
          groupedAlerts[alert.alert_id] = {
            AlertId: alert.alert_id,
            AlertType: alert.alert_type === "TEAMS" ? "Teams" : "Email",
            ChannelName: "",
            WebhookLink: "",
            Emails: [],
          };

          let config = alert.alert_config;
          if (typeof config === "string") {
            try { config = JSON.parse(config); } catch (e) { console.error("Error parsing alert_config:", e); }
          }

          if (alert.alert_type === "TEAMS") {
            groupedAlerts[alert.alert_id].ChannelName = config?.channel_name || "";
            groupedAlerts[alert.alert_id].WebhookLink = config?.webhook_link || "";
          } else if (alert.alert_type === "EMAIL") {
            if (config?.email) {
              const emails =
                typeof config.email === "string"
                  ? config.email.split(",").map((e) => e.trim())
                  : Array.isArray(config.email)
                    ? config.email
                    : [config.email];
              groupedAlerts[alert.alert_id].Emails = emails;
            }
          }
        }
      });

      const allEmails = new Set();
      const emailAlertIds = [];
      Object.values(groupedAlerts).forEach((alert) => {
        if (alert.AlertType === "Email" && alert.Emails.length > 0) {
          alert.Emails.forEach((email) => allEmails.add(email));
          emailAlertIds.push(alert.AlertId);
        }
      });

      const combinedAlerts = [];
      if (allEmails.size > 0) {
        combinedAlerts.push({
          AlertId: emailAlertIds[0],
          AlertType: "Email",
          Emails: Array.from(allEmails),
          AllAlertIds: emailAlertIds,
        });
      }
      Object.values(groupedAlerts).forEach((alert) => {
        if (alert.AlertType === "Teams") combinedAlerts.push(alert);
      });

      setAlertsData({
        ProductInfo: {
          productId: row.productId,
          productName: row.productName,
          productDescription: row.productDescription,
        },
        SLAandAlerts: { slaResponseTime: 99, slaUptime: 99, Alerts: combinedAlerts },
      });
    } catch (error) {
      console.error("Error fetching alerts data:", error);
      setAlertsData({
        ProductInfo: {
          productId: row.productId,
          productName: row.productName,
          productDescription: row.productDescription,
        },
        SLAandAlerts: { slaResponseTime: 99, slaUptime: 99, Alerts: [] },
      });
    } finally {
      setAlertsLoading(false);
    }
  };

  const refreshAlertsData = async () => {
    if (!selectedRowData?.productId) return;
    setAlertsLoading(true);
    try {
      const alertsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/alerts/get/${selectedRowData.productId}`
      );

      const groupedAlerts = {};
      alertsResponse.data.forEach((alert) => {
        if (!groupedAlerts[alert.alert_id]) {
          groupedAlerts[alert.alert_id] = {
            AlertId: alert.alert_id,
            AlertType: alert.alert_type === "TEAMS" ? "Teams" : "Email",
            ChannelName: "",
            WebhookLink: "",
            Emails: [],
          };

          let config = alert.alert_config;
          if (typeof config === "string") {
            try { config = JSON.parse(config); } catch (e) { console.error("Error parsing alert_config:", e); }
          }

          if (alert.alert_type === "TEAMS") {
            groupedAlerts[alert.alert_id].ChannelName = config?.channel_name || "";
            groupedAlerts[alert.alert_id].WebhookLink = config?.webhook_link || "";
          } else if (alert.alert_type === "EMAIL") {
            if (config?.email) {
              const emails =
                typeof config.email === "string"
                  ? config.email.split(",").map((e) => e.trim())
                  : Array.isArray(config.email)
                    ? config.email
                    : [config.email];
              groupedAlerts[alert.alert_id].Emails = emails;
            }
          }
        }
      });

      const allEmails = new Set();
      const emailAlertIds = [];
      Object.values(groupedAlerts).forEach((alert) => {
        if (alert.AlertType === "Email" && alert.Emails.length > 0) {
          alert.Emails.forEach((email) => allEmails.add(email));
          emailAlertIds.push(alert.AlertId);
        }
      });

      const combinedAlerts = [];
      if (allEmails.size > 0) {
        combinedAlerts.push({
          AlertId: emailAlertIds[0],
          AlertType: "Email",
          Emails: Array.from(allEmails),
          AllAlertIds: emailAlertIds,
        });
      }
      Object.values(groupedAlerts).forEach((alert) => {
        if (alert.AlertType === "Teams") combinedAlerts.push(alert);
      });

      setAlertsData({
        ProductInfo: {
          productId: selectedRowData.productId,
          productName: selectedRowData.productName,
          productDescription: selectedRowData.productDescription,
        },
        SLAandAlerts: { slaResponseTime: 99, slaUptime: 99, Alerts: combinedAlerts },
      });
    } catch (error) {
      console.error("Error refreshing alerts data:", error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const ConfirmVersiong = async (id) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/initiate-versioning/${id}`);
      if (response?.status === 200) {
        setToast({ open: true, message: "Versioning Initiated Successfully.", severity: "success" });
        setVersioningOpen(false);
        navigate(`/versioning/${id}`);
      }
    } catch (err) {
      setToast({ open: true, message: "Failed to Initiate Versioning.", severity: "error" });
    }
  };

  const handleDeleteClick = (productId, productName) => {
    setProductToDelete({ id: productId, name: productName });
    setDeleteDialogOpen(true);
  };

  const handleVersioningPopup = (id, name, currentPhase) => {
    if (currentPhase === "In Versioning") {
      window.location.href = `/versioning/${id}`;
    } else {
      setVersioningProduct(name);
      setVersioningId(id);
      setVersioningOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (productToDelete && onDelete) onDelete(productToDelete.id);
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const hanldeVersioning = () => {
    if (versioningId) ConfirmVersiong(versioningId);
    setVersioningOpen(false);
    setVersioningProduct(null);
    setVersioningId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handlePromoteClick = async (productId, productName) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/trigger-promotion/${productId}?triggered_by=${userEmail}`
      );
      showToast(`"${productName}" promoted successfully`);
      if (onRefresh) onRefresh();
    } catch (error) {
      showFailedToast(`"${productName}" promotion failed`);
    }
  };

  const handleVersioningCancel = () => {
    setVersioningOpen(false);
    setVersioningProduct(null);
    setVersioningId(null);
  };

  const sortedData = React.useMemo(() => {
    if (!orderBy) return data;
    return [...data].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (typeof aVal === "string") { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      if (typeof aVal === "number") return order === "asc" ? aVal - bVal : bVal - aVal;
      if (order === "asc") return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      else return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
  }, [data, order, orderBy]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "product onboarded": return "#abf2d3ff";
      case "inactive": return "error";
      case "in progress": return "#e6dbf2ff";
      case "draft": return "info";
      default: return "#f6ebf2ff";
    }
  };

  const getTextColor = (status) => {
    switch (status.toLowerCase()) {
      case "product onboarded": return "#214838ff";
      case "inactive": return "error";
      case "in progress": return "#8b36e6ff";
      case "draft": return "info";
      default: return "#cd0b7fff";
    }
  };

  const getQualityScoreColor = (score) => {
    if (score > 85) return "#4CAF50";
    if (score >= 55) return "#FF9800";
    if (score < 55) return "#F44336";
    return "#000";
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: "none", maxHeight: "100%", overflowY: "auto", overflowX: "auto" }}
        className="font-poppins"
      >
       <Table
  stickyHeader
  sx={{ 
    "& tbody .MuiTableCell-root": { borderBottom: "none" },
    "& thead .MuiTableCell-root": { 
      backgroundColor: "#1a0020 !important",
      color: "#fff !important",
    },
  }}
>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1a0020 !important",color: "#fff !important" }}>
              {columns.filter((item) => item.id !== "versioning").map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    width:
                      selectedView === "products"
                        ? column.id === "productName" ? "15%"
                          : column.id === "productDescription" ? "35%"
                            : column.id === "currentPhase" ? "14%"
                              : column.id === "elevation_status" ? "14%"
                                : column.id === "status" ? "14%"
                                  : column.id === "actions" ? "14%"
                                    : "auto"
                        : "auto",
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleRequestSort(column.id)}
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#404040 !important",
                        "&.MuiTableSortLabel-root": { color: "#fff !important" },
                        "&.MuiTableSortLabel-root.Mui-active": { color: "#fff !important" },
                        "&.MuiTableSortLabel-root:hover": { color: "#fff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#fff !important" },
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <Box sx={{ fontWeight: 600, fontSize: "12px", color: "#fff" }}>
                      {column.label}
                    </Box>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow key={row.id || index} sx={{ border: "none", fontSize: "18px" ,background:'#eee' }}>
                {selectedView === "products" ? (
                  <>

                    <TableCell sx={{ fontWeight: 500, width: "15%", fontSize: "12px !important" }}>
                      {row.productName}
                    </TableCell>

                    <TableCell
                      sx={{ fontSize: "12px !important", whiteSpace: "normal", overflow: "hidden" }}
                      title={row.productDescription}
                    >
                      {(() => {
                        const words = row.productDescription?.split(/\s+/) || [];
                        return words.slice(0, 15).join(" ") + (words.length > 15 ? " ..." : "");
                      })()}
                    </TableCell>

                    <TableCell sx={{ fontSize: "12px !important" }}>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>{row?.version || 1}</Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: "12px !important" }}>
                      <Chip
                        label={row.status}
                        size="small"
                        variant="filled"
                        sx={{
                          backgroundColor: getStatusColor(row.status),
                          color: getTextColor(row.status),
                          fontSize: "12px !important",
                          border: "none",
                          padding: "5px 0",
                          borderRadius: "6px",
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ fontSize: "12px !important" }}>{row.currentPhase || "N/A"}</TableCell>

                    {/* Elevation Status column */}
                    <TableCell sx={{ fontSize: "12px !important" }}>
                      {!isProd && (row.elevation_status && row.elevation_status === "N/A" && row?.status?.trim() === "Product Onboarded" && row?.currentPhase?.trim()?.toLowerCase() === "data contract publish") ? "Eligible" : (row.elevation_status && row.elevation_status !== "N/A" ? row.elevation_status : "Not Eligible")}
                    </TableCell>

                    <TableCell sx={{ width: "10%", fontSize: "12px !important", paddingRight: "20px !important" }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "nowrap", alignItems: "center" }}>
                        <Box sx={{ minWidth: "80px" }}>
                          {!isProd && (() => {
                            const dataEngineerVendorRoles = ["Data Engineer", "Vendor"];
                            const enabledPhasesForDataEngineerVendor = [
                              "Data Asset Selection",
                              "Column Selection",
                              "Profiling",
                              "Data Quality Rule Configuration",
                            ];

                            const isEditDisabled =
                              row.currentPhase === "Data Contract Publish" ||
                              row.currentPhase === "In Versioning" ||
                              (dataEngineerVendorRoles.includes(userRole) &&
                                !enabledPhasesForDataEngineerVendor.includes(row.currentPhase));

                            const canEdit =
                              userRole === "Admin" ||
                              (userRole === "Product Owner" && userProductNames.includes(row?.productName));

                            if (isEditDisabled) {
                              return (
                                <PermissionTooltip
                                  show={!canEdit}
                                  title="You don't have permission to edit this product."
                                  description={
                                    <span>
                                      Only product owners or authorized users can make changes.Raise access{" "}
                                      <a
                                        href="/feature-requests"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-yellow-700 underline font-medium hover:text-yellow-900"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        request
                                      </a>{" "}
                                      under Other.
                                    </span>
                                  }
                                >
                                  <span>
                                    <button
                                      onClick={() => canEdit && handleVersioningPopup(row.productId, row.productName, row.currentPhase)}
                                      disabled={!canEdit}
                                      className={`p-2 text-[12px] whitespace-nowrap font-semibold flex-shrink-0 border w-[80px]
                                    ${canEdit
                                          ? 'text-[#6366f1] border-[#6366f1] hover:bg-[#f5f3ff] cursor-pointer'
                                          : 'text-gray-300 bg-gray-100 cursor-not-allowed'
                                        }`}
                                    >
                                      <SquarePen size={14} className="inline mr-1" />
                                      Edit-V{row?.version || 1}
                                    </button>
                                  </span>
                                </PermissionTooltip>
                              );
                            }

                            return (
                              <Link
                                to={canEdit ? `/product-onboarding/${row.productId}` : "#"}
                                onClick={(e) => { if (!canEdit) e.preventDefault(); }}
                              >
                                <button
                                  disabled={!canEdit}
                                  data-mtm-action="click"
                                  data-mtm-category="Edit"
                                  data-mtm-name={row.productName}
                                  className={`w-[80px] font-semibold p-2 text-[12px] whitespace-nowrap flex-shrink-0 border
                                    ${canEdit
                                      ? "text-[#6366f1] border-[#6366f1] hover:bg-[#f5f3ff] cursor-pointer"
                                      : "text-gray-300 bg-gray-100 cursor-not-allowed"
                                    }`}
                                >
                                  <SquarePen size={14} className="inline mr-1" />
                                  Edit-V1
                                </button>
                              </Link>
                            );
                          })()}
                        </Box>

                        <button
                          data-mtm-action="click"
                          data-mtm-category="View"
                          data-mtm-name={row.productName}
                          onClick={() => onView && onView(row)}
                          className="text-[#bb1ccc] p-2 text-[12px] font-semibold whitespace-nowrap flex-shrink-0 border border-[#bb1ccc] hover:bg-[#f0fdfa]"
                        >
                          <Eye size={14} className="text-[#bb1ccc] inline mr-1" />
                          View
                        </button>

                        <button
                          onClick={(e) => handleMenuOpen(e, row)}
                          className="p-2 border border-gray-200 rounded"
                        >
                          <MoreVertical size={16} />
                        </button>

                        <Menu
                          anchorEl={menuAnchor}
                          open={Boolean(menuAnchor) && menuRow?.productId === row.productId}
                          onClose={handleMenuClose}
                        >
                          <MenuItem onClick={() => { handleAlertsClick(menuRow); handleMenuClose(); }}>
                            <Bell size={14} className="inline mr-2" /> Alert
                          </MenuItem>
                          {localStorage.getItem("role") === "Admin" && (
                            <MenuItem onClick={() => { openEdit(menuRow); handleMenuClose(); }}>
                              <PenBox size={14} className="inline mr-2 text-indigo-600" />
                              Configure Your Products
                            </MenuItem>
                          )}

                          {!isProd &&
                            (userRole === "Admin" ||
                              (userRole === "Product Owner" &&
                                userProductNames.includes(menuRow?.productName))) && (
                              <MenuItem
                                onClick={() => { handleDeleteClick(menuRow.productId, menuRow.productName); handleMenuClose(); }}
                              >
                                <Trash2 size={14} className="inline mr-2 text-red-600" />
                                Delete
                              </MenuItem>
                            )}

                          {(userRole === "Admin"
                            ? !isProd && !isEng && (menuRow?.status?.trim() === "Product Onboarded" && menuRow?.currentPhase?.trim()?.toLowerCase() === "data contract publish")
                            : !isProd &&
                            !isEng &&
                            promoteUserRole &&
                            userProductNames.includes(menuRow?.productName) &&
                            menuRow?.status?.trim() === "Product Onboarded") && (
                              <MenuItem
                                onClick={() => {
                                  if (menuRow?.elevation_status?.trim()?.toLowerCase() !== "live in production") {
                                    handlePromoteClick(menuRow.productId, menuRow.productName);
                                    handleMenuClose();
                                  }
                                }}
                                disabled={menuRow?.elevation_status?.trim()?.toLowerCase() === "promotion in progress"}
                              >
                                {menuRow?.elevation_status?.trim()?.toLowerCase() === "live in production" ? (
                                  <>
                                    <Upload size={14} className="inline mr-2 text-green-600 cursor-not-allowed" />
                                    <span className="cursor-not-allowed">Live in Prod (V{menuRow?.version || 12})</span>
                                  </>
                                ) : (
                                  <>
                                    <Upload size={14} className="inline mr-2 text-blue-600" />
                                    <span>Promote to Prod</span>
                                  </>
                                )}
                              </MenuItem>
                            )}

                          <MenuItem onClick={() => handleOpenDQModal(menuRow)}>
                            <CirclePlay size={14} className="inline mr-2 text-purple-600" />
                            Run DQ Checks
                          </MenuItem>

                          <MenuItem
                            onClick={() => handleExportClick(menuRow)}
                            disabled={exportingProducts.has(menuRow?.productId)}
                          >
                            <Download
                              size={14}
                              className={`inline mr-2 ${exportingProducts.has(menuRow?.productId) ? "text-gray-400" : "text-emerald-600"}`}
                            />
                            {exportingProducts.has(menuRow?.productId) ? "Exporting..." : "Export"}
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ fontWeight: 500 }}>{row.name}</TableCell>
                    <TableCell>{row.schema}</TableCell>
                    <TableCell>{row.columns}</TableCell>
                    <TableCell sx={{ color: getQualityScoreColor(row.qualityScore), fontWeight: 600 }}>
                      {row.qualityScore}
                    </TableCell>
                    <TableCell>{row.lastUpdated}</TableCell>
                    <TableCell>{row.createdDate}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={versioningOpen} onClose={handleVersioningCancel}>
        <DialogTitle>Confirm Versioning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to do versioning for "{versioningProduct}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVersioningCancel} color="primary">Cancel</Button>
          <Button onClick={hanldeVersioning} color="success" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the product "{productToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAlertsModal} onClose={() => setOpenAlertsModal(false)} maxWidth="md" fullWidth>
        <DialogContent>
          {alertsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
              <p className="text-gray-600 text-lg">Loading alerts data...</p>
            </div>
          ) : alertsData ? (
            <SLAandAlerts
              publishData={alertsData}
              onDataRefresh={refreshAlertsData}
              productId={selectedRowData?.productId}
              showProductSLA={false}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-500">No alerts data available.</p>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAlertsModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <EditProductModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        productId={selectedProduct?.productId}
        initialName={selectedProduct?.productName}
        product={selectedProduct}
        onSuccess={(data) => {
          setModalOpen(false);
          showToast(`"${data?.new_product_name || "N/A"}" updated successfully`);
        }}
        onFailed={(data) => {
          setModalOpen(false);
          showFailedToast(`"${data?.new_product_name || "N/A"}" updated Failed`);
        }}
      />

      <DQModal
        open={dqModal}
        onClose={() => setDqModal(false)}
        productId={dqProduct.productId}
        productName={dqProduct.productName}
        onSuccess={(data) => {
          setDqModal(false);
          showToast(`${data?.message || "DQ job Triggered Successfully"} `);
        }}
        onFailed={(data) => {
          setDqModal(false);
          showFailedToast(`${data?.message || " DQ JOb Failed to Trigger"}`);
        }}
      />
    </>
  );
};

export default MuiDataTable;